/**
 * What each child is doing in the valley, and where they are doing it.
 *
 * A port of apps/display/public/avatar-activities.js (V12), held to it by
 * activities.parity.test.ts, which loads the display's own file and compares.
 * The display replaced walking children with HELD activities -- one child,
 * one activity, one slow loop at a station -- assigned per half-day, so every
 * device shows the same valley and a reload changes nothing.
 *
 * Anchors are the display's, tuned by eye against its paintings. They move
 * when the display's move; change them there first.
 */

export interface Activity {
  id: string;
  label: string;
  posture: string;
  camera: string;
  station: string;
  frames: number;
  hold: [number, number];
  expression: string;
  head: { x: number; y: number; scale: number; angle: number };
  eyes: Array<[number, number]>;
  front: string[];
  note?: string;
  /** From the painted activity.json, when the art has landed. */
  ground?: number;
  hairStyles?: number[];
  hairBounds?: Record<string, number[]>;
  hairFit?: { fromX: number; fromY: number; scale: number; rotation?: number; targetX?: number; targetY?: number };
  eyeScale?: [number, number];
  eyeScales?: Array<[number, number]>;
  poseFits?: Array<Partial<Activity>>;
  playback?: { fps: number; order: number[]; interpolate?: boolean };
  transitions?: Record<string, { forward: string; backward: string; scale: number }>;
  motionRoot?: string;
  gaze?: [number, number];
  interaction?: string;
  ear?: [number, number, number, number];
  companion?: { species: string; root: string; frames: Record<string, string[]> };
  outfitFits?: Record<string, Partial<Activity>>;
  bounds?: number[];
  canvas?: number[];
  outfitFrames?: Record<string, number>;
  availableOutfits?: number[];
  [key: string]: unknown;
}

/** Four outfits, each a separate painting of the same child. */
export const OUTFITS = [
  { id: 'tee-shorts', label: 'Tee and shorts' },
  { id: 'tee-jeans', label: 'Tee and jeans' },
  { id: 'dress', label: 'Long dress' },
  { id: 'shirt-skirt', label: 'Shirt and skirt' },
] as const;

// tee, stripes, hoodie -> tee; collar, overalls -> jeans; dress -> dress
const LEGACY_SHIRT = [0, 0, 0, 1, 1, 2];

export function outfitOf(spec: { outfit?: unknown; shirt?: unknown } | null | undefined): number {
  const n = spec && spec.outfit;
  if (Number.isFinite(+(n as number))) return Math.max(0, Math.min(OUTFITS.length - 1, Math.trunc(+(n as number))));
  const legacy = spec && spec.shirt;
  return Number.isFinite(+(legacy as number))
    ? LEGACY_SHIRT[Math.max(0, Math.min(5, Math.trunc(+(legacy as number))))]!
    : 0;
}

export interface Station {
  x: number;
  y: number;
  spread: number;
  label: string;
}

export const STATIONS: Record<string, Station> = {
  river: { x: 0.255, y: 0.815, spread: 0.055, label: 'the river' },
  meadow: { x: 0.500, y: 0.762, spread: 0.130, label: 'the meadow' },
  stump: { x: 0.700, y: 0.735, spread: 0.045, label: 'the stump' },
  tree: { x: 0.838, y: 0.700, spread: 0.050, label: 'the big tree' },
};

export const LIST: Activity[] = [
  { id: 'fishing', label: 'Fishing', posture: 'sitting', camera: 'side', station: 'river', frames: 2, hold: [2.4, 4.0], expression: 'patient',
    head: { x: 0.44, y: 0.20, scale: 0.20, angle: -4 }, eyes: [[0.36, 0.52], [0.64, 0.52]], front: ['water'], note: 'rod tip dips' },
  { id: 'animals', label: 'Playing with animals', posture: 'sitting', camera: 'quarter', station: 'meadow', frames: 2, hold: [1.6, 2.6], expression: 'delighted',
    head: { x: 0.50, y: 0.22, scale: 0.20, angle: 3 }, eyes: [[0.36, 0.52], [0.64, 0.52]], front: ['wildlife', 'grass'], note: 'hand reaches and pats' },
  { id: 'birds', label: 'Bird watching', posture: 'standing', camera: 'quarter', station: 'tree', frames: 1, hold: [3.0, 4.5], expression: 'wonder',
    head: { x: 0.50, y: 0.11, scale: 0.15, angle: -14 }, eyes: [[0.36, 0.52], [0.64, 0.52]], front: [], note: 'head tilt is code' },
  { id: 'writing', label: 'Writing in a book', posture: 'sitting', camera: 'quarter', station: 'stump', frames: 3, hold: [1.4, 2.2], expression: 'focused',
    head: { x: 0.50, y: 0.21, scale: 0.20, angle: 8 }, eyes: [[0.36, 0.55], [0.64, 0.55]], front: [], note: 'pencil travels left to right' },
  { id: 'bubbles', label: 'Blowing bubbles', posture: 'standing', camera: 'quarter', station: 'meadow', frames: 2, hold: [1.8, 3.0], expression: 'puffed',
    head: { x: 0.50, y: 0.11, scale: 0.15, angle: 2 }, eyes: [[0.36, 0.52], [0.64, 0.52]], front: ['bubbles'], note: 'cheeks puff' },
  { id: 'nose', label: 'Picking their nose', posture: 'sitting', camera: 'front', station: 'meadow', frames: 2, hold: [2.0, 3.4], expression: 'mischief',
    head: { x: 0.50, y: 0.21, scale: 0.20, angle: 0 }, eyes: [[0.36, 0.54], [0.64, 0.54]], front: ['grass'], note: 'hand approaches' },
  { id: 'lying', label: 'Lying in the grass', posture: 'lying', camera: 'quarter', station: 'meadow', frames: 2, hold: [2.6, 4.2], expression: 'content',
    head: { x: 0.26, y: 0.42, scale: 0.17, angle: -70 }, eyes: [[0.36, 0.52], [0.64, 0.52]], front: ['grass'], note: 'raised foot wiggles' },
  { id: 'sleep-grass', label: 'Sleeping in the grass', posture: 'lying', camera: 'side', station: 'meadow', frames: 1, hold: [4.0, 6.0], expression: 'asleep',
    head: { x: 0.24, y: 0.46, scale: 0.17, angle: -88 }, eyes: [[0.36, 0.52], [0.64, 0.52]], front: ['grass'], note: 'breathing only' },
  { id: 'sleep-sit', label: 'Sleeping sitting', posture: 'sitting', camera: 'quarter', station: 'tree', frames: 1, hold: [4.0, 6.0], expression: 'asleep',
    head: { x: 0.52, y: 0.22, scale: 0.20, angle: 14 }, eyes: [[0.36, 0.56], [0.64, 0.56]], front: ['zzz'], note: 'head nod is code' },
  { id: 'painting', label: 'Painting', posture: 'standing', camera: 'side', station: 'meadow', frames: 3, hold: [1.5, 2.4], expression: 'concentrating',
    head: { x: 0.46, y: 0.11, scale: 0.15, angle: -2 }, eyes: [[0.36, 0.52], [0.64, 0.52]], front: [], note: 'brush hand' },
  { id: 'fishing-standing', label: 'Fishing (standing)', posture: 'standing', camera: 'side', station: 'river', frames: 2, hold: [2.4, 4], expression: 'patient',
    head: { x: 0.5, y: 0.15, scale: 0.15, angle: 0 }, eyes: [[0.3, 0.52]], front: ['water'], note: 'Standing on the bank; rod tip dips. Artwork pending generation.' },
  { id: 'standing', label: 'Standing portrait', posture: 'standing', camera: 'front', station: 'meadow', frames: 1, hold: [3.3, 5.7], expression: 'calm',
    head: { x: 0.5, y: 0.131, scale: 0.14, angle: 0 }, eyes: [[0.28, 0.55], [0.665, 0.55]], front: [] },
];

export const BY_ID: Record<string, Activity> = Object.fromEntries(LIST.map((a) => [a.id, a]));

/** The three the display's brief accepts first. */
export const FIRST = ['fishing', 'animals', 'lying'];
/** The activities with real paintings. */
export const PAINTED = ['fishing', 'animals', 'lying', 'fishing-standing'];
/** How wide an activity is drawn relative to a child standing. */
export const BULK: Record<string, number> = { fishing: 1.8, 'fishing-standing': 1.75, lying: 2.1, animals: 1.3 };
/** [cropped view width, measured ear-to-ear head width], in source pixels. */
export const PAINTED_MEASURES: Record<string, readonly [number, number]> = {
  fishing: [880, 200], 'fishing-standing': [870, 205], animals: [830, 315],
  lying: [1490, 280], standing: [430, 236],
};
const ANCHOR: Record<string, [number, number]> = {
  fishing: [0.84, 0.80], 'fishing-standing': [0.655, 0.625], animals: [0.45, 0.79], lying: [0.235, 0.86],
};

/**
 * FISHING CHILDREN SIT OR STAND ON A ROCK, ON EVERY SCREEN (2026-10-12).
 *
 * Meadow children are placed by fractions of the SCREEN: any grass will do.
 * A fishing child needs one particular rock, and the painting is fitted to
 * the screen by `cover`, so a rock's place on screen moves with the screen's
 * shape -- at 4:3, on a tablet held upright or on a phone, a screen fraction
 * lands the child on grass, or off the edge. So rocks are given in PAINTING
 * fractions and mapped through the same camera the animals use.
 *
 * `at` is where the child touches the rock: the seat for the sitting pose,
 * the feet for the standing one. `face` is the way the rod points: -1 is the
 * painting as drawn (rod to the left, over water to the left of the rock);
 * +1 is the same child mirrored, on a rock with the water to its right.
 *
 * Options are tried in order; the first that keeps the whole child and rod
 * on screen wins. When the rock the painting was drawn around is cut off by
 * the crop, the child crosses to a rock on the other bank and turns round.
 */
interface Rock { at: [number, number]; face: 1 | -1 }
const ROCKS: Record<'landscape' | 'portrait', Record<string, Rock[]>> = {
  landscape: {
    fishing: [
      { at: [0.861, 0.737], face: -1 },
      { at: [0.632, 0.814], face: 1 },
      { at: [0.586, 0.69], face: 1 },
    ],
    'fishing-standing': [
      { at: [0.681, 0.625], face: -1 },
      { at: [0.545, 0.606], face: 1 },
    ],
  },
  // On a phone the river's right bank is cropped away, so every portrait
  // rock is on the left bank and the child faces right, over the falls.
  portrait: {
    fishing: [
      { at: [0.70, 0.735], face: 1 },
      { at: [0.715, 0.668], face: 1 },
    ],
    'fishing-standing': [
      { at: [0.665, 0.668], face: 1 },
      { at: [0.60, 0.633], face: 1 },
      { at: [0.665, 0.725], face: 1 },
    ],
  },
};

/**
 * Where on the drawn child the rock is touched, from the sprite's
 * bottom-centre, in draw widths, for the painting as drawn (rod left). And
 * how far the child and rod reach, left and right, in the same units.
 */
const CONTACT: Record<string, { dx: number; dy: number; reach: [number, number] }> = {
  fishing: { dx: 0.2, dy: -0.337, reach: [-0.38, 0.47] },
  'fishing-standing': { dx: 0.33, dy: -0.03, reach: [-0.32, 0.45] },
};

/** `camera` from wildlife/layout.ts: the painting fitted by cover, centred. */
function fit(width: number, height: number) {
  const portrait = width < 700 && height > width;
  const sw = portrait ? 900 : 1672;
  const sh = portrait ? 1600 : 941;
  const scale = Math.max(width / sw, height / sh);
  return { portrait, width: sw * scale, height: sh * scale, x: (width - sw * scale) / 2, y: (height - sh * scale) / 2 };
}

export function isOnRock(activity: Activity | undefined): boolean {
  return Boolean(activity && CONTACT[activity.id]);
}

/**
 * Every rock this child could use on this screen, best first: the ones that
 * keep the whole child and rod in view, in the order given, then the rest by
 * how much of them is seen.
 */
export function rockSpots(activity: Activity, width: number, height: number): Placed[] {
  const st = STATIONS[activity.station] || STATIONS.meadow!;
  const cam = fit(width, height);
  const contact = CONTACT[activity.id];
  if (!contact) return [];
  const options = ROCKS[cam.portrait ? 'portrait' : 'landscape'][activity.id] ?? [];
  const margin = Math.min(8, width * 0.01);
  const spots = options.map((rock, order) => {
    const scale = (0.8 + (rock.at[1] - 0.5) * 1.8) * Math.max(0.85, Math.min(1.3, height / 941));
    const w = drawWidth(activity, scale, width, height);
    const flip = rock.face === 1 ? -1 : 1;
    const x = cam.x + rock.at[0] * cam.width - flip * contact.dx * w;
    const y = cam.y + rock.at[1] * cam.height - contact.dy * w;
    const reach = flip === 1 ? contact.reach : [-contact.reach[1], -contact.reach[0]];
    const left = x + reach[0]! * w;
    const right = x + reach[1]! * w;
    const seen = Math.max(0, Math.min(right, width - margin) - Math.max(left, margin)) / (right - left);
    return { spot: { x, y, scale, station: st, width: w, mirror: rock.face === 1 }, seen: seen >= 0.999 ? 1 : seen, order };
  });
  spots.sort((a, b) => b.seen - a.seen || a.order - b.order);
  return spots.map((one) => one.spot);
}

export function anchorOf(activity: Activity): [number, number] {
  const st = STATIONS[activity.station] || STATIONS.meadow!;
  return ANCHOR[activity.id] || [st.x, st.y];
}

export function hash(value: unknown): number {
  let n = 2166136261;
  for (const c of String(value)) n = Math.imul(n ^ c.charCodeAt(0), 16777619) >>> 0;
  return n;
}

/** A deterministic shuffle, seeded by the period. High bits, never n % k. */
export function shuffled<T>(list: readonly T[], seed: unknown): T[] {
  const out = [...list];
  let n = hash('shuffle:' + seed);
  for (let i = out.length - 1; i > 0; i--) {
    n = (Math.imul(n, 1664525) + 1013904223) >>> 0;
    const j = Math.floor((n / 4294967296) * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/** Which day it is, locally. The activity changes when this does. */
export function dayNumber(now?: number | Date | null): number {
  const d = now ? new Date(now) : new Date();
  return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000);
}

/** Two periods a day: the valley changes at dawn and again at dusk. */
export function periodNumber(now: number | Date | null | undefined, night: boolean): number {
  return dayNumber(now) * 2 + (night ? 1 : 0);
}

interface Person {
  id: string;
  name?: string;
}

function deal(ordered: Person[], period: number, salt: string): Map<string, Activity> {
  const pool = shuffled(PAINTED.filter((id) => BY_ID[id]), period + salt);
  if (!pool.length) return new Map(ordered.map((p) => [p.id, BY_ID[FIRST[0]!]!]));
  const picks = shuffled(ordered.map((_, i) => i), period + salt + ':order');
  const out = new Map<string, Activity>();
  picks.forEach((who, turn) => out.set(ordered[who]!.id, BY_ID[pool[turn % pool.length]!]!));
  return out;
}

const sameDeal = (a: Map<string, Activity>, b: Map<string, Activity>) =>
  a.size === b.size && [...a].every(([id, act]) => b.get(id) === act);

const LOOKBACK = 3;
function resolve(ordered: Person[], period: number, depth: number): Map<string, Activity> {
  let out = deal(ordered, period, '');
  if (depth <= 0 || ordered.length < 2) return out;
  const before = resolve(ordered, period - 1, depth - 1);
  for (const salt of [':again', ':again2']) {
    if (!sameDeal(out, before)) break;
    out = deal(ordered, period, salt);
  }
  return out;
}

let memo: { key: string | null; value: Map<string, Activity> | null } = { key: null, value: null };

/** Everybody doing something different, and never the same valley twice running. */
export function assignRoster(people: Person[], now: number | Date | null | undefined, night: boolean): Map<string, Activity> {
  const ordered = [...people].sort((a, b) => String(a.id).localeCompare(String(b.id)));
  const period = periodNumber(now, night);
  const key = period + '|' + ordered.map((p) => p.id).join(',');
  if (memo.key === key) return memo.value!;
  const value = resolve(ordered, period, LOOKBACK);
  memo = { key, value };
  return value;
}

export const FRAME_KEYS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;

/** Deterministic per child per period. */
export function assign(person: Person | null, now: number | Date | null | undefined, pool: string[] | null, night: boolean): Activity {
  let from = (pool && pool.length ? pool : FIRST).filter((id) => BY_ID[id]);
  if (!from.length) from = FIRST;
  const key = (person && (person.id || person.name)) || 'anon';
  const n = hash(key + ':' + periodNumber(now, night));
  return BY_ID[from[n % from.length]!]!;
}

export interface Placed {
  id?: string;
  activity?: Activity;
  x: number;
  y: number;
  scale: number;
  station: Station;
  width: number;
  /** Drawn mirrored: a fishing child on the other bank, rod to the right. */
  mirror?: boolean;
}

export function drawWidth(activity: Activity, scale: number, width: number, height: number): number {
  const phone = width < 700 && height > width;
  const measure = PAINTED_MEASURES[activity.id];
  // Keep fishing's reviewed size; match every other head to it at this depth.
  if (measure) return (phone ? 78 : 74) * scale * 1.8 * (200 / 880) * measure[0] / measure[1];
  return (phone ? 78 : 74) * scale * (BULK[activity.id] || 1);
}

export function place(activity: Activity, slot: number, count: number, width: number, height: number): Placed {
  if (isOnRock(activity)) {
    return rockSpots(activity, width, height)[0] ?? { x: width * 0.5, y: height * 0.8, scale: 1, station: STATIONS.river!, width: drawWidth(activity, 1, width, height) };
  }
  const st = STATIONS[activity.station] || STATIONS.meadow!;
  const n = Math.max(1, count);
  const offset = n === 1 ? 0 : (slot / (n - 1) - 0.5) * st.spread;
  const depth = ((slot * 7) % 3) / 3;
  const anchor = anchorOf(activity);
  const x = (anchor[0] + offset) * width;
  const y = (anchor[1] + depth * 0.028) * height;
  const scale = (0.8 + (anchor[1] - 0.5) * 1.8) * Math.max(0.85, Math.min(1.3, height / 941));
  return { x, y, scale, station: st, width: drawWidth(activity, scale, width, height) };
}

const gapX = (a: Placed, b: Placed) => (a.width + b.width) * 0.34;
const gapY = (a: Placed, b: Placed) => (a.width + b.width) * 0.17;

/**
 * Two children whose drawn bodies cover each other. The nominal gap above is
 * for children fanned out on grass; two fishing children on neighbouring
 * rocks can pass it and still have one standing in the other's lap.
 */
function crowds(a: Placed, b: Placed): boolean {
  const box = (p: Placed) => [p.x - p.width * 0.4, p.y - p.width * 0.95, p.x + p.width * 0.4, p.y] as const;
  const [ax0, ay0, ax1, ay1] = box(a);
  const [bx0, by0, bx1, by1] = box(b);
  const ix = Math.max(0, Math.min(ax1, bx1) - Math.max(ax0, bx0));
  const iy = Math.max(0, Math.min(ay1, by1) - Math.max(ay0, by0));
  const smaller = Math.min((ax1 - ax0) * (ay1 - ay0), (bx1 - bx0) * (by1 - by0));
  return ix * iy > smaller * 0.15;
}

/**
 * The part of a drawn child an animal must not walk through or rest on, as
 * a screen box: left, top, right, bottom. Fractions of draw width from the
 * sprite's bottom-centre, for the painting as drawn. A fishing child's rod
 * and line count: a duck bobbing at the end of the line reads as a duck that
 * has been caught. Mirrored children flip it. See wildlife/layout.ts `clear`.
 */
const BODY: Record<string, [number, number, number]> = {
  fishing: [-0.46, 0.5, -0.97],
  'fishing-standing': [-0.4, 0.5, -1.06],
  animals: [-0.48, 0.48, -1.1],
  lying: [-0.52, 0.52, -0.5],
};
export function bodyBox(p: Placed): [number, number, number, number] {
  const [l, r, top] = BODY[p.activity?.id ?? ''] ?? [-0.42, 0.42, -1.05];
  const [x0, x1] = p.mirror ? [-r, -l] : [l, r];
  return [p.x + x0 * p.width, p.y + top * p.width, p.x + x1 * p.width, p.y + 0.04 * p.width];
}

export function overlaps(placed: Placed[]): Array<[Placed, Placed]> {
  const bad: Array<[Placed, Placed]> = [];
  for (let i = 0; i < placed.length; i++) {
    for (let j = i + 1; j < placed.length; j++) {
      const a = placed[i]!, b = placed[j]!;
      const close = isOnRock(a.activity) && isOnRock(b.activity)
        ? crowds(a, b)
        : Math.abs(a.x - b.x) < gapX(a, b) && Math.abs(a.y - b.y) < gapY(a, b);
      if (close) bad.push([a, b]);
    }
  }
  return bad;
}

/** A phone holds two children at a readable size; a wall holds four. */
export function capacity(width: number, height: number): number {
  return width < 700 && height > width ? 2 : 4;
}

export function separate(placed: Placed[], width: number, height: number): Placed[] {
  const reach = width * 0.06;
  const onRock = (entry: Placed) => isOnRock(entry.activity);
  // A child on a rock is already where the painting needs them; clamping
  // would slide them off it. Everyone else stays inside the frame.
  const hold = (entry: Placed) => {
    if (onRock(entry)) return;
    entry.x = Math.max(entry.width * 0.5, Math.min(width - entry.width * 0.5, entry.x));
    entry.y = Math.max(height * 0.45, Math.min(height * 0.93, entry.y));
  };
  placed.forEach(hold);
  for (let pass = 0; pass < 8; pass++) {
    const clashes = overlaps(placed);
    if (!clashes.length) break;
    for (const [a, b] of clashes) {
      // Move the meadow resident, never a child anchored to a river rock.
      if (onRock(a) || onRock(b)) {
        // Two children fishing on the same rock: the second takes the next
        // rock that is in view and clear of the first.
        if (onRock(a) && onRock(b)) {
          // The one standing moves; a seated child keeps the better rock.
          const [still, moving] = b.activity?.id === 'fishing-standing' ? [a, b] : [b, a];
          const other = rockSpots(moving.activity!, width, height).find(
            (spot) => !crowds(still, { ...moving, ...spot }),
          );
          if (other) Object.assign(moving, other);
          continue;
        }
        const fixed = onRock(a) ? a : b, free = fixed === a ? b : a;
        free.x = fixed.x + (free.x < fixed.x ? -1 : 1) * (gapX(a, b) + 1);
        hold(free);
        if (Math.abs(free.x - fixed.x) < gapX(a, b)) {
          free.y = fixed.y - gapY(a, b) - 1;
          hold(free);
        }
        continue;
      }
      const left = a.x <= b.x ? a : b, right = a.x <= b.x ? b : a;
      const need = gapX(a, b) - Math.abs(a.x - b.x) + 1;
      const was = Math.abs(left.x - right.x);
      const room = Math.min(need / 2, reach);
      left.x -= room;
      right.x += room;
      hold(left);
      hold(right);
      if (Math.abs(left.x - right.x) - was < need - 0.5) {
        const step = gapY(a, b) - Math.abs(a.y - b.y) + 1;
        const down = right.y + step <= height * 0.93;
        right.y += down ? step : -step;
        right.scale *= down ? 1.06 : 0.94;
        right.width = drawWidth(right.activity!, right.scale, width, height);
        hold(right);
      }
    }
  }
  return placed;
}

export function layout(people: Person[], assignments: Map<string, Activity>, width: number, height: number): Placed[] {
  const groups: Record<string, Array<{ person: Person; activity: Activity }>> = {};
  const ordered = [...people].sort((a, b) => String(a.id).localeCompare(String(b.id)));
  for (const person of ordered) {
    const activity = assignments.get(person.id);
    if (!activity) continue;
    (groups[activity.station] = groups[activity.station] || []).push({ person, activity });
  }
  const placed: Placed[] = [];
  for (const group of Object.values(groups)) {
    group.forEach((entry, slot) =>
      placed.push({ id: entry.person.id, activity: entry.activity, ...place(entry.activity, slot, group.length, width, height) }),
    );
  }
  return separate(placed, width, height);
}
